import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col,Tooltip, Drawer, Flex, Grid } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { App } from "antd";
import { fetchWarehousesList, deleteWarehouse } from "../warehousesThunks";
import { setSelectedWarehouse, setIsEditWarehouse } from "../warehousesSlice";
import { selectWarehouses } from "../warehousesSelectors";
import dayjs from "dayjs";
import { getAccessType } from "../../../../lib/functions";
import { useIsAdminUser } from "../../../../lib/accessList";

const WarehouseList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let [access_type, access_type_set] = useState("");
  const warehouses = useSelector(selectWarehouses);
  const authInfo = useSelector((state) => state.auth?.auth_info);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  useEffect(() => {
    dispatch(fetchWarehousesList());
  }, [dispatch]);

  useEffect(() => {
    const access = getAccessType("warehouse_entry");
    access_type_set(access);
  }, []);

  const handleDelete = async (id, name) => {
    modal.confirm({
      title: "Are you sure?",
      content: `This will delete warehouse: ${name}`,
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(deleteWarehouse(id)).unwrap();
          modal.success({ title: "Warehouse deleted successfully" });
          dispatch(fetchWarehousesList());
        } catch (error) {
          modal.error({ title: error.message || "Delete failed" });
        }
      },
    });
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Warehouse Name",
      dataIndex: "name",
    },
    {
      title: "Warehouse Title",
      dataIndex: "title",
    },
    {
      title: "Warehouse Address",
      dataIndex: "address",
    },
    {
      title: "Created Date & Time",
      dataIndex: "created_date",
      render: (value) => (value ? dayjs(value).format("DD-MMM-YYYY") : "-"),
    },
    ...(access_type === "WRITE"
      ? [
        {
          title: "Actions",
          render: (_, record) => (
            <>
              <Button
                type="link"
                onClick={() => {
                  dispatch(setSelectedWarehouse(record));
                  dispatch(setIsEditWarehouse(true));
                  navigate("/settings/warehouses/addNewWarehouse");
                }}
                icon={<EditOutlined />}
                disabled={!(isAdminUser || canEdit)}
              />

              <Button
                type="link"
                danger
                onClick={() => handleDelete(record.id, record.name)}
                icon={<DeleteOutlined />}
                disabled={!(isAdminUser || canDelete)}
              />
            </>
          ),
        },
      ]
      : []),
  ];

  return (
    <>
      <Row justify="end" style={{ marginBottom: "32px", marginTop: "32px" }}>
        {/* <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedWarehouse(null));
                  dispatch(setIsEditWarehouse(false));
                  navigate("addNewWarehouse");
                }}
              >
                Add New
              </Button>
            </>
          )}
        </Col> */}
        <Col>
          <Flex
            justify="end"
            align="center"
            gap={6}
            style={{ width: "100%" }}
          >
            <Tooltip title="Help">
              <Button
                type="text"
                icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
                onClick={() => setHelpOpen(true)}
              />
            </Tooltip>

            {access_type == "WRITE" && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedWarehouse(null));
                  dispatch(setIsEditWarehouse(false));
                  navigate("addNewWarehouse");
                }}
              >
                Add New
              </Button>
            )}
          </Flex>
        </Col>
        
      </Row>
      <Drawer
        title="Warehouse Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 12,
              marginLeft: 12
            }}
          >
            Warehouse Management
          </h2>

          <p style={{ color: "#555", marginBottom: 16, marginLeft:17 }}>
            This section helps manage warehouses, inventory storage areas,
            and stock handling operations used throughout the ERP system.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: 14,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <strong>What You Can Do</strong>

            <ol style={{ paddingLeft: 20, marginTop: 10 }}>
              <li>Create and manage warehouse records</li>
              <li>Update warehouse names and titles</li>
              <li>Manage warehouse addresses and storage details</li>
              <li>Track warehouse creation dates</li>
              <li>Edit warehouse information when required</li>
              <li>Delete warehouses if permitted</li>
              <li>Organize inventory across multiple warehouses</li>
              <li>Manage stock storage workflows efficiently</li>
              <li>Maintain warehouse-based inventory operations</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: 14,
              borderRadius: 8,
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: 18,
                marginTop: 10,
                color: "#666",
              }}
            >
              <li>Warehouses are linked with inventory stock workflows.</li>
              <li>Deleting warehouses may affect existing inventory records.</li>
              <li>Warehouse addresses help organize stock locations properly.</li>
              <li>Only authorized users can edit or delete warehouse records.</li>
              <li>Warehouse management improves stock tracking accuracy.</li>
            </ul>
          </div>
        </div>
      </Drawer>
      <Table
        columns={columns}
        dataSource={warehouses}
        rowKey="id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
    </>
  );
};

export default WarehouseList;
